import {
  AirplaneTiltIcon, GearSixIcon, HouseIcon, ReceiptIcon, UsersThreeIcon,
  type Icon,
} from '@phosphor-icons/react';
import { matchPath } from 'react-router-dom';

import type { SpaceKind, SpaceModel } from '@/types/spaces';

// A space has three destinations (2026-09-23): the home, the list of expenses and the space's settings. Acerto and
// the budget are sheets over the home, not pages: they show the same numbers the home already leads with.
export type SpacePage = 'home' | 'expenses' | 'settings';
export type SpaceSheet = 'settle' | 'budget';
/** `href` may carry the period; `matchPrefix` is the bare path an active-state check compares against. */
export type SpaceNavItem = { id: SpacePage; href: string; matchPrefix: string; label: string; icon: Icon };
type NavigableSpace = { id: string; kind: SpaceKind };

export const SPACE_KIND_LABELS: Record<SpaceKind, string> = { house: 'Casa', trip: 'Viagem', other: 'Outro' };
export const SPACE_PAGE_LABELS: Record<SpacePage, string> = { home: 'Início', expenses: 'Despesas', settings: 'Configurações' };
/** The home's query parameter that opens a sheet; the address owns it, so Back closes the sheet and a link opens it. */
export const SPACE_SHEET_PARAM = 'sheet';
const SPACE_SHEETS: ReadonlySet<string> = new Set<SpaceSheet>(['settle', 'budget']);

export function spaceIcon(kind: SpaceKind): Icon {
  return kind === 'trip' ? AirplaneTiltIcon : kind === 'house' ? HouseIcon : UsersThreeIcon;
}

export function spaceHref(id: string, page: SpacePage = 'home'): string {
  return `/spaces/${encodeURIComponent(id)}/${page}`;
}

/** The home with one of its sheets open. */
export function spaceSheetHref(id: string, sheet: SpaceSheet): string {
  return `${spaceHref(id, 'home')}?${new URLSearchParams({ [SPACE_SHEET_PARAM]: sheet }).toString()}`;
}

/** The sheet an address asks for, if it names one this space's home has: Acerto needs the split model's ledger. */
export function spaceSheetFromParams(params: URLSearchParams, model: SpaceModel): SpaceSheet | null {
  const sheet = params.get(SPACE_SHEET_PARAM);
  if (!sheet || !SPACE_SHEETS.has(sheet)) return null;
  return sheet === 'settle' && model !== 'split' ? null : sheet as SpaceSheet;
}

// Without `period` in the address the picker falls back to the last STORED selection, so a link between two space
// pages must carry the period the reader is looking at, or a bookmarked / typed range silently becomes another one.
const PERIOD_PARAMS = ['period', 'from', 'to'] as const;

/** A space page that keeps the period params of the current address as they are ("Mês atual" stays "Mês atual"). */
export function spaceHrefKeepingPeriod(id: string, page: SpacePage, current: URLSearchParams): string {
  const params = new URLSearchParams();
  for (const name of PERIOD_PARAMS) {
    const value = current.get(name);
    if (value) params.set(name, value);
  }
  const query = params.toString();
  return query ? `${spaceHref(id, page)}?${query}` : spaceHref(id, page);
}

/**
 * A space page showing one space period: a month key (`yyyy-MM`) becomes that calendar month as an explicit range;
 * a trip has one period, so its pages need none. `extra` adds the destination's own params (a Despesas filter).
 */
export function spaceHrefForPeriod(id: string, page: SpacePage, period: string, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams();
  const month = /^(\d{4})-(\d{2})$/.exec(period);
  if (month) {
    const lastDay = new Date(Date.UTC(Number(month[1]), Number(month[2]), 0)).getUTCDate();
    params.set('period', 'custom');
    params.set('from', `${period}-01`);
    params.set('to', `${period}-${String(lastDay).padStart(2, '0')}`);
  }
  for (const [name, value] of Object.entries(extra)) params.set(name, value);
  const query = params.toString();
  return query ? `${spaceHref(id, page)}?${query}` : spaceHref(id, page);
}

/** Scope is URL-owned: deep links and history never depend on a saved selection. */
export function spaceIdFromPath(pathname: string): string | null {
  const id = matchPath('/spaces/:spaceId/*', pathname)?.params.spaceId;
  return id && id.toLowerCase() !== 'new' && id.toLowerCase() !== 'rules' ? id : null;
}

export function spacePageFromPath(pathname: string): SpacePage | null {
  const page = matchPath('/spaces/:spaceId/:page', pathname)?.params.page?.toLowerCase();
  return page && Object.hasOwn(SPACE_PAGE_LABELS, page) ? page as SpacePage : null;
}

// `current` is the address being read: its period rides along on every item, so moving between space pages from the
// sidebar or the bottom bar keeps the range on screen (see `spaceHrefKeepingPeriod`).
export function spaceNavItems(space: NavigableSpace, surface: 'sidebar' | 'mobile', current?: URLSearchParams): SpaceNavItem[] {
  const item = (id: SpacePage, icon: Icon, label = SPACE_PAGE_LABELS[id]): SpaceNavItem => ({
    id, label, icon,
    href: current ? spaceHrefKeepingPeriod(space.id, id, current) : spaceHref(space.id, id),
    matchPrefix: spaceHref(space.id, id),
  });
  return [
    item('home', spaceIcon(space.kind)),
    item('expenses', ReceiptIcon),
    item('settings', GearSixIcon, surface === 'mobile' ? 'Espaço' : 'Configurações'),
  ];
}

/** Analytics records route patterns, never a member/space identifier. */
export function spaceTelemetryTarget(href: string): string {
  // A nav href can carry the period; the event records the route, not the query.
  const pathname = href.split('?')[0];
  return spaceIdFromPath(pathname) ? pathname.replace(/^\/spaces\/[^/]+/i, '/spaces/:spaceId') : pathname;
}
